import React, { useState, useEffect, useRef } from 'react';
import { useAppContext } from '../context/AppContext';
import { getAllCategories, getSubcategories } from '../utils/categorization';
import { useModalA11y } from '../utils/useModalA11y';

function RetagModal({ txnIdx, currentFilter, onClose, onSaved }) {
    const { filteredTransactions, retagTransaction, isSampleMode, triggerSampleLocked, setStatus } = useAppContext();
    const txn = filteredTransactions[txnIdx];

    const [categoryInput, setCategoryInput] = useState(txn?.category || '');
    const [subcategoryInput, setSubcategoryInput] = useState(txn?.subcategory || '');
    const [applyAll, setApplyAll] = useState(true);
    const [showCatSuggestions, setShowCatSuggestions] = useState(false);
    const [showSubSuggestions, setShowSubSuggestions] = useState(false);

    const catWrapperRef = useRef(null);
    const subWrapperRef = useRef(null);

    useModalA11y({ onClose, isOpen: Boolean(txn) });

    useEffect(() => {
        function handleClickOutside(e) {
            if (catWrapperRef.current && !catWrapperRef.current.contains(e.target)) {
                setShowCatSuggestions(false);
            }
            if (subWrapperRef.current && !subWrapperRef.current.contains(e.target)) {
                setShowSubSuggestions(false);
            }
        }
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    const categories = getAllCategories();
    const filteredCategories = categories.filter(c => c.toLowerCase().includes(categoryInput.toLowerCase()));

    const subcategories = getSubcategories(categoryInput);
    const filteredSubcategories = subcategories.filter(s => s.toLowerCase().includes(subcategoryInput.toLowerCase()));

    const selectCategory = (cat) => {
        setCategoryInput(cat);
        setShowCatSuggestions(false);
        const subs = getSubcategories(cat);
        setSubcategoryInput(subs[0] || '');
    };

    const selectSubcategory = (sub) => {
        setSubcategoryInput(sub);
        setShowSubSuggestions(false);
    };

    const handleSave = () => {
        if (isSampleMode) { triggerSampleLocked(); onClose(); return; }
        const newCategory = categoryInput.trim();
        const newSubcategory = subcategoryInput.trim();

        if (!newCategory || !newSubcategory) {
            alert('Please select both category and subcategory');
            return;
        }

        (async () => {
            const updated = await retagTransaction(txnIdx, newCategory, newSubcategory, applyAll);
            const n = typeof updated === 'number' ? updated : (applyAll ? 'all' : 1);
            setStatus({
                active: true,
                text: `Remembered · ${n} payment${n === 1 ? '' : 's'} updated. Same name auto-tags next time.`,
                type: 'success',
            });
            if (onSaved) onSaved(updated);
            onClose();
        })();
    };

    if (!txn) return null;

    return (
        <div className="retag-modal" role="presentation">
            <div className="retag-modal-backdrop" onClick={onClose} aria-hidden="true" />
            <div
                className="retag-modal-content"
                role="dialog"
                aria-modal="true"
                aria-labelledby="retag-modal-title"
            >
                <div className="retag-modal-header">
                    <h3 id="retag-modal-title">Re-tag merchant</h3>
                    <button type="button" className="retag-modal-close" onClick={onClose} aria-label="Close">×</button>
                </div>
                <div className="retag-modal-body">
                    <div className="retag-txn-info">
                        <div className="retag-merchant">{txn.party || 'Unknown Merchant'}</div>
                        <div className="retag-current">{txn.category} · {txn.subcategory}</div>
                        <p className="retag-hint">Remembered on this device. Pro vault moves your rules to a new phone.</p>
                    </div>

                    <div className="retag-form-group">
                        <label htmlFor="retag-category">Category</label>
                        <div className="retag-select-wrapper" ref={catWrapperRef}>
                            <input
                                id="retag-category"
                                type="text"
                                className="retag-input"
                                placeholder="Search or select category..."
                                value={categoryInput}
                                onChange={(e) => { setCategoryInput(e.target.value); setShowCatSuggestions(true); }}
                                onFocus={() => setShowCatSuggestions(true)}
                                autoComplete="off"
                                role="combobox"
                                aria-expanded={showCatSuggestions}
                                aria-controls="retag-cat-list"
                            />
                            {showCatSuggestions && (
                                <div id="retag-cat-list" className="retag-suggestions active" role="listbox">
                                    {filteredCategories.map(c => (
                                        <button
                                            type="button"
                                            key={c}
                                            role="option"
                                            className="retag-suggestion-item"
                                            onClick={() => selectCategory(c)}
                                        >
                                            {c}
                                        </button>
                                    ))}
                                    {categoryInput && !categories.some(c => c.toLowerCase() === categoryInput.toLowerCase()) && (
                                        <button
                                            type="button"
                                            className="retag-suggestion-item custom-tag"
                                            onClick={() => selectCategory(categoryInput.trim())}
                                        >
                                            Add “{categoryInput.trim()}” as custom category
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="retag-form-group">
                        <label htmlFor="retag-subcategory">Subcategory</label>
                        <div className="retag-select-wrapper" ref={subWrapperRef}>
                            <input
                                id="retag-subcategory"
                                type="text"
                                className="retag-input"
                                placeholder="Search or select subcategory..."
                                value={subcategoryInput}
                                onChange={(e) => { setSubcategoryInput(e.target.value); setShowSubSuggestions(true); }}
                                onFocus={() => setShowSubSuggestions(true)}
                                autoComplete="off"
                                role="combobox"
                                aria-expanded={showSubSuggestions}
                                aria-controls="retag-sub-list"
                            />
                            {showSubSuggestions && (
                                <div id="retag-sub-list" className="retag-suggestions active" role="listbox">
                                    {filteredSubcategories.map(s => (
                                        <button
                                            type="button"
                                            key={s}
                                            role="option"
                                            className="retag-suggestion-item"
                                            onClick={() => selectSubcategory(s)}
                                        >
                                            {s}
                                        </button>
                                    ))}
                                    {subcategoryInput && !subcategories.some(s => s.toLowerCase() === subcategoryInput.toLowerCase()) && (
                                        <button
                                            type="button"
                                            className="retag-suggestion-item custom-tag"
                                            onClick={() => selectSubcategory(subcategoryInput.trim())}
                                        >
                                            Add “{subcategoryInput.trim()}” as custom subcategory
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="retag-apply-all">
                        <label className="retag-checkbox-label">
                            <input type="checkbox" checked={applyAll} onChange={(e) => setApplyAll(e.target.checked)} />
                            <span>Apply to all payments from this merchant</span>
                        </label>
                    </div>
                </div>
                <div className="retag-modal-footer">
                    <button type="button" className="retag-btn-cancel" onClick={onClose}>Cancel</button>
                    <button type="button" className="retag-btn-save" onClick={handleSave}>Save changes</button>
                </div>
            </div>
        </div>
    );
}

export default RetagModal;
